/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { or } from '@ember/object/computed';
import { service } from '@ember/service';
import { classNameBindings, classNames } from '@ember-decorators/component';
import PostTypes from '@linc-technologies/linced-common/enums/post-type';
import {
  findBy,
  resolved,
} from '@linc-technologies/linced-common/utils/arrays';
import { saveOrDiscard } from '@linc-technologies/linced-common/utils/record-rollback';

@classNames('reactions', 'layout-row', 'layout-align-space-between-center')
@classNameBindings('active:active')
export default class ReactionsWidget extends Component {
  // Component versioning
  @tracked visualStyle = 'v2';

  @service
  session;

  @service
  store;

  @service
  toaster;

  @tracked
  reactionsOpen = false;
  buttonElement = null;
  parentElement = null;
  @tracked
  hideReactionsHandler = null;
  active = false;
  actionsActive = false;

  @or('commentId', 'postId')
  entity;

  popperOptions = Object.freeze({
    modifiers: {
      preventOverflow: {
        escapeWithReference: false,
      },
    },
  });

  @computed('type')
  get available() {
    switch (this.type) {
      case 2: // Facebook-style reactions
        return [
          {
            value: 1,
            icon: 'like',
            label: 'Like',
            emoji: '👍',
          },
          {
            value: 2,
            icon: 'love',
            label: 'Love',
            emoji: '😍',
          },
          {
            value: 3,
            icon: 'haha',
            label: 'Haha',
            emoji: '😆',
          },
          {
            value: 4,
            icon: 'wow',
            label: 'Wow',
            emoji: '😲',
          },
          {
            value: 5,
            icon: 'sad',
            label: 'Sad',
            emoji: '😢',
          },
        ];
      case 3: // SOLO Taxonomy
        return [
          {
            value: 1,
            icon: 'prestructural',
            label: 'Prestructural',
          },
          {
            value: 2,
            icon: 'unistructural',
            label: 'Unistructural',
          },
          {
            value: 3,
            icon: 'multistructural',
            label: 'Multistructural',
          },
          {
            value: 4,
            icon: 'relational',
            label: 'Relational',
          },
          {
            value: 5,
            icon: 'extended',
            label: 'Extended Abstract',
          },
        ];
      default:
        return [];
    }
  }

  @computed('reactions.content.@each.commentId', 'commentId', 'type')
  get filteredReactions() {
    let reactions = resolved(this.reactions);
    let commentId = this.commentId;

    if (!commentId) {
      reactions = reactions.filter(function (reaction) {
        if (reaction.get('commentId.id')) {
          return false;
        }
        return true;
      });
    }

    return reactions.filter((reaction) => reaction.reactionType === this.type);
  }

  @computed('type', 'reactions.content.@each.reaction')
  get counts() {
    let available = this.available;
    let reactions = this.filteredReactions;
    let counts = [];

    available.forEach(function (reaction) {
      let count = reactions.filter((r) => r.reaction === reaction.value).length;
      if (count) {
        counts.push({ count, reaction });
      }
    });

    return counts;
  }

  @computed('type', 'session.personId', 'reactions.[]')
  get userReactionRecord() {
    let personId = this.get('session.personId');
    return this.filteredReactions.find((r) => r.authorId === personId);
  }

  @computed(
    'type',
    'session.personId',
    'reactions.[]',
    'userReactionRecord.reaction',
  )
  get userReaction() {
    let reaction = this.userReactionRecord;
    if (reaction && reaction.get('reactionType') === this.type) {
      return findBy(this.available, 'value', reaction.get('reaction'));
    }
    return null;
  }

  // `(action "hide" target=popover)` retargeted the action at the popover block
  // param. `{{on}}`/`{{fn}}` have no `target=`, so call it explicitly.
  @action
  hidePopover(popover) {
    popover.hide();
  }

  @action
  showReactions() {
    this.reactionsOpen = true;
  }

  @action
  hideReactions() {
    this.hideReactionsHandler = setTimeout(() => {
      this.reactionsOpen = false;
    }, 1500);
  }

  @action
  async react(reaction) {
    let record = this.userReactionRecord;

    if (record) {
      if (record.reaction === reaction.value) {
        // Delete reaction if user re-clicks the same reaction.
        return record
          .destroyRecord()
          .then(() => {
            this.reactionsOpen = false;
          })
          .catch(() => {
            // Clear the deleted flag, or the reaction stays hidden.
            record.rollbackAttributes();
            this.toaster.error('Error removing the reaction.');
          });
      }

      // Update current record to a different reaction i.e. like -> love
      record.set('reaction', reaction.value);
    } else {
      // Create new reaction
      // A belongsTo takes a record, not the async proxy.
      let post = await this.postId;
      if (this.isDestroying || this.isDestroyed) {
        return;
      }
      let postType = post.get('postType');
      record = this.store.createRecord('posts/reaction', {
        schoolId: this.get('session.activeSchool'),
        commentId: this.commentId,
        type: this.type,
        reaction: reaction.value,
        authorId: this.get('session.personId'),
        postType,
      });
      switch (postType) {
        case PostTypes.COMMUNITY_NOTICE:
          record.set('communityNoticeId', post);
          break;
        case PostTypes.STUDENT_NOTICE:
          record.set('studentNoticeId', post);
          break;
        default:
          record.set('postId', post);
          break;
      }
    }

    // A failed new reaction is discarded and a failed change put back, so the
    // counts match the server.
    saveOrDiscard(record).catch(() =>
      this.toaster.error('Error saving the reaction.'),
    );
    this.reactionsOpen = false;
  }
}
